import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mic, Loader2, Lock, Play, Sparkles, ArrowRight, ShieldCheck } from "lucide-react";

export const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const REG = {
  "voice-analysis-for-health-insights": { type: "scan" },
  "health-memberships": { type: "membership" },
  "digital-healings": { type: "audio", kind: "healings" },
  "inner-genie-meditations": { type: "audio", kind: "meditations" },
  "body-frequency-mapping": { type: "info", cta: "scan" },
  "identify-health-risks": { type: "info", cta: "membership" },
  "precision-sound-frequency-prescriptions": { type: "info", cta: "membership" },
};

const TIERS = [
  { key: "membership_30", name: "30 Days", price: "₹5,555", benefits: ["Unlimited scans for 30 days", "All health reports (Emotional, Blood)", "Daily frequency prescription", "Email support"] },
  { key: "membership_60", name: "60 Days", price: "₹9,999", highlight: true, benefits: ["Everything in 30 days", "Inner Genie meditations", "Digital Healings library", "Priority support"] },
  { key: "membership_90", name: "90 Days", price: "₹12,999", benefits: ["Everything in 60 days", "20-minute prescriptions", "Scan history & comparison", "Priority 1:1 support"] },
];

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(undefined);
  const reg = REG[slug] || { type: "info", cta: "scan" };

  useEffect(() => {
    api.get("/content/products").then((r) => {
      setProduct(r.data.find((p) => slugify(p.title) === slug) || null);
    }).catch(() => setProduct(null));
  }, [slug]);

  if (product === undefined) return <div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-primary" strokeWidth={1.5} /></div>;

  const title = product?.title || slug.replace(/-/g, " ");
  const description = product?.description || "";
  const image = product?.image_url;

  return (
    <div>
      <section className="relative overflow-hidden border-b border-border">
        {image && <><img src={mediaUrl(image)} alt="" className="absolute inset-0 w-full h-full object-cover" /><div className="absolute inset-0 bg-background/85" /></>}
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-24">
          <Link to="/products" className="text-sm text-muted-foreground hover:text-primary transition-colors">← All products</Link>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-5 mb-4 max-w-3xl">{title}</h1>
          <p className="text-lg text-secondary-foreground/90 max-w-2xl leading-relaxed">{description}</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16">
        {reg.type === "scan" && <ScanCTA user={user} navigate={navigate} />}
        {reg.type === "membership" && <Membership user={user} navigate={navigate} />}
        {reg.type === "audio" && <AudioLibrary kind={reg.kind} user={user} />}
        {reg.type === "info" && <Info cta={reg.cta} />}
      </div>
    </div>
  );
}

function ScanCTA({ user, navigate }) {
  const go = () => {
    if (!user || typeof user !== "object") return navigate("/register");
    return navigate("/app/scan");
  };
  return (
    <div className="text-center max-w-2xl mx-auto">
      <span className="grid place-items-center h-16 w-16 rounded-full bg-primary/15 text-primary mx-auto mb-6"><Mic strokeWidth={1.5} /></span>
      <h2 className="font-serif text-3xl mb-3">Your free voice scan</h2>
      <p className="text-muted-foreground mb-8">Thirty seconds of sound reveal the frequencies your body resonates with. We turn them into a clear Physical Health Mapping across octaves 4–6 — with text, PDF and audio narration.</p>
      <Button size="lg" onClick={go} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/85 font-semibold h-12 px-8" data-testid="detail-take-scan-btn">Take your free scan <ArrowRight size={18} strokeWidth={1.5} /></Button>
      <p className="text-xs text-muted-foreground mt-8 border-t border-border pt-6">This is a wellness insight, not a medical diagnosis. Always confirm any concern with a qualified physician.</p>
    </div>
  );
}

function Membership({ user, navigate }) {
  const [loading, setLoading] = useState(null);
  const subscribe = async (key) => {
    if (!user || typeof user !== "object") return navigate("/login");
    setLoading(key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setLoading(null);
    }
  };
  return (
    <div>
      <h2 className="font-serif text-3xl mb-8 text-center">Choose your membership</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {TIERS.map((t) => (
          <div key={t.key} className={`rounded-2xl p-7 flex flex-col ${t.highlight ? "border-2 border-primary bg-primary/5" : "border border-border bg-card"}`} data-testid={`tier-${t.key}`}>
            {t.highlight && <span className="text-[10px] uppercase tracking-[0.2em] text-primary mb-2">Most popular</span>}
            <h3 className="font-serif text-2xl">{t.name}</h3>
            <div className="my-4"><span className="font-serif text-3xl">{t.price}</span></div>
            <ul className="space-y-2 text-sm text-muted-foreground flex-1 mb-6">
              {t.benefits.map((b) => <li key={b} className="flex items-start gap-2"><Sparkles size={13} className="text-primary shrink-0 mt-1" strokeWidth={1.5} /> {b}</li>)}
            </ul>
            <Button onClick={() => subscribe(t.key)} disabled={loading === t.key} className={`w-full rounded-full font-semibold ${t.highlight ? "bg-primary text-primary-foreground hover:bg-primary/85" : "bg-secondary text-secondary-foreground hover:bg-secondary/70"}`} data-testid={`subscribe-${t.key}`}>
              {loading === t.key ? <Loader2 size={16} className="animate-spin" /> : null} Subscribe
            </Button>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-8 text-center">Secure checkout via Stripe (test mode). Use card 4242 4242 4242 4242, any future expiry, any CVC.</p>
    </div>
  );
}

function AudioLibrary({ kind, user }) {
  const [tracks, setTracks] = useState(null);
  const [member, setMember] = useState(false);
  useEffect(() => {
    api.get(`/content/${kind}`).then((r) => setTracks(r.data)).catch(() => setTracks([]));
    if (user && typeof user === "object") api.get("/membership").then((r) => setMember(!!r.data.active)).catch(() => {});
  }, [kind, user]);

  if (tracks === null) return <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-primary" strokeWidth={1.5} /></div>;
  if (tracks.length === 0) return <p className="text-center text-muted-foreground py-12">Tracks are being curated. Check back soon.</p>;

  return (
    <div className="space-y-3">
      {tracks.map((t, i) => {
        const unlocked = i === 0 || member;
        return (
          <div key={t.id} className="rounded-xl border border-border bg-card p-4 flex items-center gap-4" data-testid={`track-${i}`}>
            <span className="grid place-items-center h-11 w-11 rounded-lg bg-secondary text-primary shrink-0">{unlocked ? <Play size={18} strokeWidth={1.5} /> : <Lock size={18} strokeWidth={1.5} />}</span>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{t.title}{i === 0 && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-primary/15 text-primary">Free</span>}</p>
              {t.description && <p className="text-xs text-muted-foreground truncate">{t.description}</p>}
            </div>
            {unlocked ? (
              <audio controls src={mediaUrl(t.audio_url)} className="h-9 max-w-[240px]" data-testid={`track-audio-${i}`} />
            ) : (
              <Link to="/products/health-memberships"><Button size="sm" variant="outline" className="rounded-full border-border" data-testid={`track-lock-${i}`}>Subscribe to access</Button></Link>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Info({ cta }) {
  const isScan = cta === "scan";
  return (
    <div className="max-w-2xl mx-auto text-center">
      <span className="grid place-items-center h-16 w-16 rounded-full bg-accent/20 text-accent-foreground mx-auto mb-6"><ShieldCheck strokeWidth={1.5} /></span>
      <p className="text-secondary-foreground leading-relaxed mb-8">Your voice carries frequency patterns that map to areas of wellbeing. This feature interprets those patterns responsibly — as indicators to observe, never as a diagnosis — and pairs them with sound-frequency guidance you can act on.</p>
      <Link to={isScan ? "/products/voice-analysis-for-health-insights" : "/products/health-memberships"}>
        <Button size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/85 font-semibold h-12 px-8" data-testid="info-cta-btn">
          {isScan ? "Take a free scan to see a preview" : "Unlock this with a Health Membership"} <ArrowRight size={18} strokeWidth={1.5} />
        </Button>
      </Link>
    </div>
  );
}
